import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faTag } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import SEO from '../SEO';
import LandingHeader from '../landing/LandingHeader';
import { getServiceBySlug, getRelatedServices } from '../../data/services';
import { generateBreadcrumbJsonLd } from '../../utils/seoData';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';

const ServiceDetailPage: React.FC = () => {
  const { slug, lang } = useParams<{ slug: string; lang: string }>();
  const navigate = useNavigate();
  // @ts-ignore
  const { t } = useTranslation('services');
  const i18nInstance = i18n;
  const service = slug ? getServiceBySlug(slug, i18nInstance) : undefined;
  const relatedServices = service ? getRelatedServices(service, 3, i18nInstance) : [];

  if (!service) {
    return (
      <>
        <LandingHeader />
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">{t('serviceNotFound')}</h1>
            <p className="text-gray-400 mb-8">{t('serviceNotFoundDescription')}</p>
            <Link
              to={`/${lang}/services`}
              className="inline-block px-6 py-3 bg-purple-600 md:hover:bg-purple-700 rounded-lg transition-colors"
            >
              {t('backToServices')}
            </Link>
          </div>
        </div>
      </>
    );
  }

  const currentLang = lang || i18nInstance.language || 'tr';
  const categoryLabels: Record<string, string> = {
    'all': t('categories.all'),
    'social-media': t('categories.socialMedia'),
    'ai-tools': t('categories.aiTools'),
    'analytics': t('categories.analytics'),
    'web-analytics': t('categories.webAnalytics'),
    'advertising': t('categories.advertising')
  };

  const breadcrumbJsonLd = generateBreadcrumbJsonLd([
    { name: currentLang === 'en' ? 'Home' : 'Ana Sayfa', url: `https://winnomi.com/${currentLang}/home` },
    { name: currentLang === 'en' ? 'Services' : 'Hizmetler', url: `https://winnomi.com/${currentLang}/services` },
    { name: service.title, url: `https://winnomi.com/${currentLang}/services/${service.slug}` },
  ]);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: 'Winnomi AI',
      url: 'https://winnomi.com',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Turkey',
    },
    serviceType: service.category,
  };

  return (
    <>
      <SEO
        title={`${service.title} | ${t('title')}`}
        description={service.seo.metaDescription}
        keywords={service.seo.keywords.join(', ')}
        image={service.seo.ogImage || `/services/${service.slug}.jpg`}
        type="website"
        jsonLd={[breadcrumbJsonLd, serviceJsonLd]}
        locale={currentLang === 'en' ? 'en_US' : 'tr_TR'}
        alternateLanguages={{
          'tr': `https://winnomi.com/tr/services/${service.slug}`,
          'en': `https://winnomi.com/en/services/${service.slug}`,
          'x-default': `https://winnomi.com/tr/services/${service.slug}`
        }}
      />
      <LandingHeader />
      <div className="min-h-screen bg-gray-900 text-white mt-[-64px] bg-gradient-to-br from-purple-900/20 via-gray-900 to-gray-900">
        {/* Header */}
        <div className="border-b border-white/10 pt-20">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <button
              onClick={() => navigate(`/${lang}/services`)}
              className="flex items-center gap-2 text-gray-400 md:hover:text-white mb-6 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>{t('backToServices')}</span>
            </button>

            <div className="mb-4">
              <span className={`inline-block px-4 py-2 ${service.bgColor} ${service.color} rounded-full text-sm`}>
                {categoryLabels[service.category] || service.category}
              </span>
            </div>

            <div className="aspect-video max-w-4xl mx-auto mb-8 rounded-2xl overflow-hidden">
              <img
                src={service.seo.ogImage || `/services/${service.id}.jpg`}
                alt={service.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.parentElement;
                  if (fallback) {
                    fallback.className = `aspect-video ${service.bgColor} flex items-center justify-center rounded-2xl`;
                    fallback.innerHTML = `<span class="text-6xl">${service.icon}</span>`;
                  }
                }}
              />
            </div>

            <div className="flex items-center gap-4 mb-6">
              {/* <div className={`text-6xl ${service.bgColor} p-4 rounded-2xl`}>
                {service.icon}
              </div> */}
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 leading-tight">
                  {service.title}
                </h1>
                {service.comingSoon && (
                  <span className="inline-block text-sm text-yellow-400 bg-yellow-400/20 px-3 py-1 rounded-full">
                    {t('comingSoon')}
                  </span>
                )}
              </div>
            </div>

            <p className="text-xl text-gray-300">
              {service.description}
            </p>
          </div>
        </div>

        {/* Content */}
        <article className="max-w-4xl mx-auto px-6 py-12">
          {/* Main Content */}
          <div className="prose prose-invert prose-lg max-w-none mb-12">
            <div className="markdown-content">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="text-3xl font-bold mt-8 mb-4">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-2xl font-bold mt-6 mb-3">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-xl font-semibold mt-4 mb-2">{children}</h3>,
                  p: ({ children }) => <p className="mb-4 text-gray-300 leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-2 text-gray-300">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-300">{children}</ol>,
                  li: ({ children }) => <li className="ml-4">{children}</li>,
                  code: ({ children }) => (
                    <code className="bg-white/10 px-2 py-1 rounded text-sm font-mono">{children}</code>
                  ),
                  pre: ({ children }) => (
                    <pre className="bg-white/5 p-4 rounded-lg overflow-x-auto mb-4">
                      {children}
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-purple-500 pl-4 italic text-gray-400 my-4">
                      {children}
                    </blockquote>
                  ),
                  strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-400 md:hover:text-purple-300 underline"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {service.content}
              </ReactMarkdown>
            </div>
          </div>

          {/* Features Section */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6">{t('features')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {service.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCheck} className={`${service.color} mt-1`} />
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6">{t('benefits')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {service.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCheck} className={`${service.color} mt-1`} />
                  <span className="text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Metrics Section */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6">{t('metrics')}</h2>
            <div className="flex flex-wrap gap-2">
              {service.metrics.map((metric, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-gray-300"
                >
                  {metric}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* Related Services */}
        {relatedServices.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 py-12 border-t border-white/10">
            <h2 className="text-2xl font-bold mb-6">{t('relatedServices')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedServices.map((relatedService) => (
                <Link
                  key={relatedService.id}
                  to={`/${lang}/services/${relatedService.slug}`}
                  className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden md:hover:border-purple-500/50 transition-all"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={relatedService.seo.ogImage || `/services/${relatedService.id}.jpg`}
                      alt={relatedService.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.parentElement;
                        if (fallback) {
                          fallback.className = `aspect-video ${relatedService.bgColor} flex items-center justify-center`;
                          fallback.innerHTML = `<span class="text-4xl">${relatedService.icon}</span>`;
                        }
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <span className={`inline-block px-3 py-1 ${relatedService.bgColor} ${relatedService.color} rounded-full text-sm mb-3`}>
                      {categoryLabels[relatedService.category] || relatedService.category}
                    </span>
                    <h3 className="text-lg font-semibold mb-2 md:group-hover:text-purple-400 transition-colors line-clamp-2">
                      {relatedService.title}
                    </h3>
                    <p className="text-gray-400 text-sm line-clamp-2">
                      {relatedService.shortDescription}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">
              {service.comingSoon ? t('cta.comingSoonTitle') : t('cta.getStarted')}
            </h2>
            <p className="text-gray-300 mb-6">
              {service.comingSoon
                ? t('cta.comingSoonDescription')
                : t('cta.description', { service: service.title.toLowerCase() })
              }
            </p>
            {!service.comingSoon && (
              <Link
                to={`/${lang}/register`}
                className="inline-block px-6 py-3 bg-purple-600 md:hover:bg-purple-700 rounded-lg transition-colors font-semibold"
              >
                {t('cta.button')}
              </Link>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default ServiceDetailPage;

